import { useState, useEffect } from "react";

interface DeviceType {
	isMobile: boolean;
	isTablet: boolean;
	isDesktop: boolean;
	screenSize: "mobile" | "tablet" | "desktop";
}

const useDeviceType = (): DeviceType => {
	const [deviceType, setDeviceType] = useState<DeviceType>(() => {
		// Default values for SSR/initial render
		if (typeof window === "undefined") {
			return {
				isMobile: false,
				isTablet: false,
				isDesktop: true,
				screenSize: "desktop",
			};
		}

		// Initial calculation
		const width = window.innerWidth;
		const isMobile = width < 768;
		const isTablet = width >= 768 && width < 1024;
		const isDesktop = width >= 1024;

		return {
			isMobile,
			isTablet,
			isDesktop,
			screenSize: isMobile ? "mobile" : isTablet ? "tablet" : "desktop",
		};
	});

	useEffect(() => {
		if (typeof window === "undefined") return;

		const mobileQuery = window.matchMedia("(max-width: 767px)");
		const tabletQuery = window.matchMedia(
			"(min-width: 768px) and (max-width: 1023px)",
		);
		const desktopQuery = window.matchMedia("(min-width: 1024px)");

		const updateDeviceType = () => {
			const isMobile = mobileQuery.matches;
			const isTablet = tabletQuery.matches;
			const isDesktop = desktopQuery.matches;

			setDeviceType({
				isMobile,
				isTablet,
				isDesktop,
				screenSize: isMobile ? "mobile" : isTablet ? "tablet" : "desktop",
			});
		};

		// Set initial state
		updateDeviceType();

		// Add listeners
		mobileQuery.addEventListener("change", updateDeviceType);
		tabletQuery.addEventListener("change", updateDeviceType);
		desktopQuery.addEventListener("change", updateDeviceType);

		// Cleanup
		return () => {
			mobileQuery.removeEventListener("change", updateDeviceType);
			tabletQuery.removeEventListener("change", updateDeviceType);
			desktopQuery.removeEventListener("change", updateDeviceType);
		};
	}, []);

	return deviceType;
};

export default useDeviceType;
